import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  MousePointerClick,
  LayoutList,
  Megaphone,
  Image as ImageIcon,
  Star,
  UtensilsCrossed,
  Newspaper,
  Mail,
  PanelBottom,
  ExternalLink,
  Wand2,
  type LucideIcon,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { MediaField } from "@/components/admin/MediaField";
import { HomepagePromotionEditor } from "@/components/admin/HomepagePromotionEditor";
import { AdminSection } from "@/components/admin/AdminSection";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  getAdminHomepage,
  updateAdminHomepage,
  updateSchema,
  type AdminHomepageContent,
} from "@/lib/admin/homepage.functions";
import { HOMEPAGE_FALLBACKS } from "@/lib/homepage-defaults";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

export const Route = createFileRoute("/_admin/admin/homepage")({
  head: () => ({ meta: [
    { title: "Homepage Settings | Poonam Kapur Healthy Kitchen" },
    { name: "description", content: "Manage homepage content and the live announcement for Poonam Kapur Healthy Kitchen." },
    { property: "og:title", content: "Homepage Settings | Poonam Kapur Healthy Kitchen" },
    { property: "og:description", content: "Manage homepage content and the live announcement." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: HomepagePage,
});

type FormState = Record<string, string>;

type FieldDef = {
  key: keyof AdminHomepageContent;
  label: string;
  /** Short sentence explaining exactly what appears on the website. */
  help: string;
  /** Realistic sample content shown as the placeholder / demo prefill. */
  demo: string;
  type?: "text" | "textarea" | "image" | "video" | "select";
  /** Options for `type: "select"`. */
  options?: Array<{ value: string; label: string }>;
  rows?: number;
  wide?: boolean;
};

type SectionDef = {
  title: string;
  icon: LucideIcon;
  description: string;
  fields: FieldDef[];
  /** Renders a live button preview from a text+link pair. */
  ctaPreview?: Array<{
    label: string;
    textKey: keyof AdminHomepageContent;
    linkKey: keyof AdminHomepageContent;
  }>;
};

const LINK_HELP =
  "Where the button goes. Internal path (starts with /) or a full https:// URL — external links open in a new tab.";

const IMAGE_HELP =
  "Direct https:// image URL. Leave blank to keep the built-in editorial photo. Save is blocked while a URL fails to load.";
const INVALID_IMAGE_URL_MSG =
  "This URL is not a direct image URL. Use a URL such as https://images.unsplash.com/… or upload the image to storage.";

const DEMO_IMAGE = {
  hero: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1600&q=80",
  whyChoose:
    "https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=1600&q=80",
  trust:
    "https://images.unsplash.com/photo-1498837167922-ddd27525d352?auto=format&fit=crop&w=1600&q=80",
  cta: "https://images.unsplash.com/photo-1543353071-873f17a7a088?auto=format&fit=crop&w=1600&q=80",
};

const SECTIONS: SectionDef[] = [
  {
    title: "Hero",
    icon: Sparkles,
    description:
      "The first thing every visitor sees — badge, headline and supporting line at the very top of the homepage.",
    fields: [
      {
        key: "hero_badge",
        label: "Badge",
        help: "Small pill above the headline. Use a location or credibility cue.",
        demo: HOMEPAGE_FALLBACKS.heroBadge,
      },
      {
        key: "hero_title_line1",
        label: "Headline — line 1",
        help: "First line of the big headline. Plain text.",
        demo: HOMEPAGE_FALLBACKS.heroTitleLine1,
      },
      {
        key: "hero_title_accent",
        label: "Headline — accent word",
        help: "Rendered in italic on the second line. Keep it to one or two words.",
        demo: HOMEPAGE_FALLBACKS.heroTitleAccent,
      },
      {
        key: "hero_title_line2",
        label: "Headline — line 2",
        help: "Text that follows the italic accent on the second line.",
        demo: HOMEPAGE_FALLBACKS.heroTitleLine2,
      },
      {
        key: "hero_subtitle",
        label: "Subtitle",
        help: "One or two sentences under the headline. Say who you cook for and where you deliver.",
        demo: HOMEPAGE_FALLBACKS.heroSubtitle,
        type: "textarea",
        rows: 3,
      },
      {
        key: "hero_image_url",
        label: "Hero image",
        help: `Full-width hero photo shown when Hero media type is Image. Not used in Video mode — the video plays on its own, with no image underneath or around it. ${IMAGE_HELP}`,
        demo: DEMO_IMAGE.hero,
        type: "image",
        wide: true,
      },
      {
        key: "hero_image_alt",
        label: "Hero image / video alt text",
        help: "Describes the hero media for screen readers and search engines. One short sentence.",
        demo: "A thoughtfully plated healthy Indian meal — dal, brown rice, roasted vegetables, roti and fresh salad.",
        wide: true,
      },
      {
        key: "hero_media_type",
        label: "Hero media type",
        help: "Choose what shows in the full-width hero: the hero image, or an autoplaying muted video. In Video mode the hero image is not used.",
        demo: "image",
        type: "select",
        options: [
          { value: "image", label: "Image" },
          { value: "video", label: "Video" },
        ],
      },
      {
        key: "hero_video_url",
        label: "Hero video",
        help: "Desktop / tablet video — landscape source works best. MP4 (H.264) or WebM, 10–20 seconds, 30 MB max. Plays muted and loops with no controls. Pick or upload it in the Media Library.",
        demo: "",
        type: "video",
        wide: true,
      },
      {
        key: "hero_video_mobile_url",
        label: "Hero video — mobile (optional)",
        help: "Portrait (9:16) version used on screens under 768px. MP4 or WebM, 30 MB max. Leave blank to use the main hero video everywhere.",
        demo: "",
        type: "video",
        wide: true,
      },
      {
        key: "hero_poster_url",
        label: "Hero video poster (optional)",
        help: "Still frame shown while the video loads, when autoplay is blocked, or when the visitor prefers reduced motion. Leave blank for no poster — the hero image is never used in Video mode.",
        demo: "",
        type: "image",
        wide: true,
      },
    ],
  },
  {
    title: "Hero buttons",
    icon: MousePointerClick,
    description:
      "The two call-to-action buttons under the hero headline. The first is the filled primary button, the second is the outline button.",
    fields: [
      {
        key: "primary_button_text",
        label: "Primary button text",
        help: "Main action. Keep it short — 2 to 3 words.",
        demo: HOMEPAGE_FALLBACKS.primaryButtonText,
      },
      {
        key: "primary_button_link",
        label: "Primary button link",
        help: LINK_HELP,
        demo: HOMEPAGE_FALLBACKS.primaryButtonLink,
      },
      {
        key: "secondary_button_text",
        label: "Secondary button text",
        help: "Softer action for visitors who aren't ready to order yet.",
        demo: HOMEPAGE_FALLBACKS.secondaryButtonText,
      },
      {
        key: "secondary_button_link",
        label: "Secondary button link",
        help: LINK_HELP,
        demo: HOMEPAGE_FALLBACKS.secondaryButtonLink,
      },
    ],
    ctaPreview: [
      { label: "Primary", textKey: "primary_button_text", linkKey: "primary_button_link" },
      { label: "Secondary", textKey: "secondary_button_text", linkKey: "secondary_button_link" },
    ],
  },
  {
    title: "Why choose us",
    icon: LayoutList,
    description:
      "Heading and intro for the benefits strip (freshness, nutrition, sourcing). The benefit cards themselves are part of the website design.",
    fields: [
      {
        key: "why_choose_heading",
        label: "Heading",
        help: "Appears above the benefit cards.",
        demo: HOMEPAGE_FALLBACKS.whyChooseHeading,
      },
      {
        key: "why_choose_description",
        label: "Description",
        help: "Optional supporting line. Leave blank to show the heading on its own.",
        demo: "Farm-direct produce, no preservatives, and portions balanced by our in-house nutritionist.",
        type: "textarea",
      },
      {
        key: "why_choose_banner_url",
        label: "Banner image",
        help: `Wide banner behind the benefits strip. ${IMAGE_HELP}`,
        demo: DEMO_IMAGE.whyChoose,
        type: "image",
        wide: true,
      },
    ],
  },
  {
    title: "How it works",
    icon: Wand2,
    description:
      "The four-step explainer that replaces the meal plans block on the homepage — eyebrow, heading, intro, four steps and one supporting photo.",
    fields: [
      {
        key: "how_it_works_eyebrow",
        label: "Eyebrow",
        help: "Small uppercase label above the heading.",
        demo: HOMEPAGE_FALLBACKS.howItWorksEyebrow,
      },
      {
        key: "how_it_works_heading",
        label: "Heading",
        help: "Main heading of the How It Works section.",
        demo: HOMEPAGE_FALLBACKS.howItWorksHeading,
      },
      {
        key: "how_it_works_description",
        label: "Introductory description",
        help: "One or two sentences under the heading.",
        demo: HOMEPAGE_FALLBACKS.howItWorksDescription,
        type: "textarea",
        rows: 3,
      },
      {
        key: "how_it_works_step1_title",
        label: "Step 1 — title",
        help: "Title of the first step card.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep1Title,
      },
      {
        key: "how_it_works_step1_description",
        label: "Step 1 — description",
        help: "Short sentence explaining the first step.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep1Description,
        type: "textarea",
      },
      {
        key: "how_it_works_step2_title",
        label: "Step 2 — title",
        help: "Title of the second step card.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep2Title,
      },
      {
        key: "how_it_works_step2_description",
        label: "Step 2 — description",
        help: "Short sentence explaining the second step.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep2Description,
        type: "textarea",
      },
      {
        key: "how_it_works_step3_title",
        label: "Step 3 — title",
        help: "Title of the third step card.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep3Title,
      },
      {
        key: "how_it_works_step3_description",
        label: "Step 3 — description",
        help: "Short sentence explaining the third step.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep3Description,
        type: "textarea",
      },
      {
        key: "how_it_works_step4_title",
        label: "Step 4 — title",
        help: "Title of the fourth step card.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep4Title,
      },
      {
        key: "how_it_works_step4_description",
        label: "Step 4 — description",
        help: "Short sentence explaining the fourth step.",
        demo: HOMEPAGE_FALLBACKS.howItWorksStep4Description,
        type: "textarea",
      },
      {
        key: "how_it_works_image_url",
        label: "Supporting image",
        help: `Single photo shown beside the four steps. ${IMAGE_HELP}`,
        demo: DEMO_IMAGE.whyChoose,
        type: "image",
        wide: true,
      },
      {
        key: "how_it_works_image_alt",
        label: "Supporting image alt text",
        help: "Describes the photo for screen readers and search engines.",
        demo: HOMEPAGE_FALLBACKS.howItWorksImageAlt,
        wide: true,
      },
    ],
  },
  {
    title: "Trust & quality",
    icon: ImageIcon,
    description:
      "The sourcing, hygiene and quality block. Its copy is part of the website design — only the photo is editable here.",
    fields: [
      {
        key: "trust_quality_image_url",
        label: "Trust image",
        help: `Photo in the sourcing / hygiene block. ${IMAGE_HELP}`,
        demo: DEMO_IMAGE.trust,
        type: "image",
        wide: true,
      },
    ],
  },
  {
    title: "Meal categories",
    icon: UtensilsCrossed,
    description:
      "Heading and intro above the four daily slots — breakfast, lunch, snacks and dinner.",
    fields: [
      {
        key: "meal_categories_heading",
        label: "Heading",
        help: "Sits above the category tiles.",
        demo: HOMEPAGE_FALLBACKS.mealCategoriesHeading,
      },
      {
        key: "meal_categories_description",
        label: "Description",
        help: "Optional line explaining how the slots work.",
        demo: "Pick any combination of slots — breakfast, lunch, evening snacks and dinner, delivered on your schedule.",
        type: "textarea",
        rows: 3,
      },
    ],
  },
  {
    title: "Founder story",
    icon: Newspaper,
    description:
      "The short founder / kitchen story block that builds trust midway down the homepage.",
    fields: [
      {
        key: "founder_heading",
        label: "Heading",
        help: "A single line summarising the kitchen's philosophy.",
        demo: HOMEPAGE_FALLBACKS.founderHeading,
      },
      {
        key: "founder_description",
        label: "Story",
        help: "Two to four sentences about the founder, the team and the sourcing.",
        demo: HOMEPAGE_FALLBACKS.founderDescription,
        type: "textarea",
        rows: 4,
      },
      {
        key: "founder_image_url",
        label: "Founder image",
        help: `Portrait shown beside the founder story. Leave empty to keep the current image. ${IMAGE_HELP}`,
        demo: "",
        type: "image",
      },
    ],
  },
  {
    title: "Final call to action",
    icon: Megaphone,
    description:
      "The large banner just above the footer — the last chance to convert a visitor.",
    fields: [
      {
        key: "cta_heading",
        label: "Heading",
        help: "A direct question or invitation.",
        demo: HOMEPAGE_FALLBACKS.ctaHeading,
      },
      {
        key: "cta_description",
        label: "Description",
        help: "One or two lines reassuring the visitor before they click.",
        demo: HOMEPAGE_FALLBACKS.ctaDescription,
        type: "textarea",
        rows: 3,
      },
      {
        key: "cta_button_text",
        label: "Button text",
        help: "Action label for the banner button.",
        demo: HOMEPAGE_FALLBACKS.ctaButtonText,
      },
      {
        key: "cta_button_link",
        label: "Button link",
        help: LINK_HELP,
        demo: HOMEPAGE_FALLBACKS.ctaButtonLink,
      },
      {
        key: "final_cta_background_url",
        label: "Closing banner image",
        help: `Background photo behind this closing banner. Choose something dark enough for white text. ${IMAGE_HELP}`,
        demo: DEMO_IMAGE.cta,
        type: "image",
        wide: true,
      },
    ],
    ctaPreview: [
      { label: "Banner button", textKey: "cta_button_text", linkKey: "cta_button_link" },
    ],
  },

];

/** Homepage blocks whose content lives in a dedicated admin module. */
const LINKED_SECTIONS: Array<{
  title: string;
  icon: LucideIcon;
  description: string;
  to: string;
  cta: string;
}> = [
  {
    title: "Featured meal plans",
    icon: UtensilsCrossed,
    description:
      "The meal-plan cards on the homepage — names, descriptions, pricing and images — are pulled live from the catalogue.",
    to: "/admin/meal-plans-v2",
    cta: "Edit meal plans",
  },
  {
    title: "Testimonials",
    icon: Star,
    description:
      "Customer names, designations, ratings, photos and quotes shown in the homepage slider.",
    to: "/admin/testimonials",
    cta: "Edit testimonials",
  },
  {
    title: "Journal preview",
    icon: Newspaper,
    description:
      "The three latest journal articles displayed near the bottom of the homepage.",
    to: "/admin/journal",
    cta: "Edit journal",
  },
  {
    title: "Newsletter signup",
    icon: Mail,
    description:
      "Heading, subtitle, button label and success message for the footer signup form, plus the subscriber list.",
    to: "/admin/newsletter",
    cta: "Open newsletter",
  },
  {
    title: "Footer & contact",
    icon: PanelBottom,
    description:
      "Address, phone, WhatsApp number, email, social links and delivery areas shown in the site footer.",
    to: "/admin/settings",
    cta: "Open site settings",
  },
];

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);

function toForm(data: AdminHomepageContent): FormState {
  const out: FormState = {};
  for (const f of ALL_FIELDS) {
    const raw = data[f.key];
    out[f.key] = typeof raw === "string" ? raw : "";
  }
  return out;
}

function HomepagePage() {
  const fetchFn = useServerFn(getAdminHomepage);
  const updateFn = useServerFn(updateAdminHomepage);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "homepage-content"],
    queryFn: () => fetchFn(),
    staleTime: 10_000,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const setImageErrorFor = (key: string, hasError: boolean) => {
    setImageErrors((prev) => {
      if (hasError === !!prev[key]) return prev;
      const next = { ...prev };
      if (hasError) next[key] = true;
      else delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  const mutation = useMutation({
    mutationFn: (patch: Record<string, string>) =>
      updateFn({ data: patch as never }),
    onSuccess: async (updated) => {
      toast.success("Homepage saved");
      setForm(toForm(updated as AdminHomepageContent));
      setErrors({});
      await queryClient.invalidateQueries({ queryKey: ["admin", "homepage-content"] });
      await queryClient.invalidateQueries({ queryKey: ["homepage-content"] });
    },
    onError: (err) => {
      console.error(err);
      toast.error("Couldn't save. Please review the fields and try again.");
    },
  });

  const announcementMutation = useMutation({
    mutationFn: (enabled: boolean) => updateFn({ data: { live_announcement_enabled: enabled } }),
    onSuccess: async () => {
      toast.success("Announcement setting saved");
      await queryClient.invalidateQueries({ queryKey: ["admin", "homepage-content"] });
      await queryClient.invalidateQueries({ queryKey: ["homepage-content"] });
    },
    onError: () => toast.error("Couldn't save the announcement setting. Please try again."),
  });

  const saved = data ? toForm(data) : null;
  const dirty = !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved);

  useUnsavedChanges(dirty);

  if (isLoading || !form) {
    return (
      <div>
        <AdminPageHeader title="Homepage" description="Edit the copy shown on the public homepage." />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError || !data || !saved) {
    return (
      <div>
        <AdminPageHeader title="Homepage" />
        <Alert variant="destructive">
          <AlertDescription>
            Couldn't load homepage content.{" "}
            <button className="underline" onClick={() => refetch()}>Retry</button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const sectionCustomised = (section: SectionDef) =>
    section.fields.some((f) => (saved[f.key] ?? "").trim().length > 0);

  const fillDemo = (section: SectionDef) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      for (const f of section.fields) {
        if (!(next[f.key] ?? "").trim()) next[f.key] = f.demo;
      }
      return next;
    });
    toast.info(`Sample content added to "${section.title}". Review it, then save.`);
  };

  const handleSave = () => {
    const patch: Record<string, string> = {};
    for (const [k, v] of Object.entries(form)) {
      if (v !== saved[k]) patch[k] = v;
    }
    if (Object.keys(patch).length === 0) {
      toast.info("No changes to save.");
      return;
    }
    const brokenImageKeys = Object.keys(imageErrors).filter((k) => imageErrors[k]);
    if (brokenImageKeys.length > 0) {
      const fe: Record<string, string> = {};
      for (const k of brokenImageKeys) fe[k] = INVALID_IMAGE_URL_MSG;
      setErrors((prev) => ({ ...prev, ...fe }));
      toast.error("Please fix or clear the highlighted image URLs.");
      return;
    }
    const result = updateSchema.safeParse(patch);
    if (!result.success) {
      const fe: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    mutation.mutate(patch);
  };

  const handleReset = () => {
    setForm(toForm(data));
    setErrors({});
    setImageErrors({});
  };

  return (
    <div className="pb-4">
      <AdminPageHeader
        title="Homepage"
        description="Every section below maps to a block on the public homepage, top to bottom. Leave a field blank to keep the built-in default."
      />
      <div className="mb-6 flex items-center justify-between gap-4 border-b border-border py-4">
        <Label htmlFor="homepage-live-announcement" className="text-base font-semibold">Homepage Live Announcement Banner</Label>
        <Switch id="homepage-live-announcement" checked={data.live_announcement_enabled} disabled={announcementMutation.isPending || dirty} onCheckedChange={(enabled) => announcementMutation.mutate(enabled)} />
      </div>
      <HomepagePromotionEditor savedConfig={data.promotion_config} />
      <div className="space-y-4">
        {SECTIONS.map((section, index) => (
          <AdminSection
            key={section.title}
            number={index + 1}
            icon={section.icon}
            title={section.title}
            description={section.description}
            customised={sectionCustomised(section)}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {section.fields.map((f) => {
                const id = `field-${f.key}`;
                const value = form[f.key] ?? "";
                const err = errors[f.key];
                const isTextarea = f.type === "textarea";
                const isImage = f.type === "image" || f.type === "video";
                const isSelect = f.type === "select";
                const spanFull = isTextarea || isImage || f.wide;
                return (
                  <div key={f.key} className={spanFull ? "min-w-0 sm:col-span-2" : "min-w-0"}>
                    <Label htmlFor={id} className="text-sm">
                      {f.label}
                    </Label>
                    {isTextarea ? (
                      <Textarea
                        id={id}
                        rows={f.rows ?? 3}
                        value={value}
                        placeholder={f.demo}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        aria-invalid={!!err}
                        className="mt-1 w-full"
                      />
                    ) : isImage ? (
                      <MediaField
                        id={id}
                        value={value}
                        allowUrlInput={false}
                        onChange={(next) => {
                          setForm({ ...form, [f.key]: next });
                          setImageErrorFor(f.key, false);
                          if (errors[f.key]) {
                            setErrors((prev) => {
                              const { [f.key]: _drop, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                        invalid={!!err}
                      />
                    ) : isSelect ? (
                      <Select
                        value={value || (f.options?.[0]?.value ?? "")}
                        onValueChange={(next) => setForm({ ...form, [f.key]: next })}
                      >
                        <SelectTrigger id={id} className="mt-1 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(f.options ?? []).map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={id}
                        type="text"
                        value={value}
                        placeholder={f.demo}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        aria-invalid={!!err}
                        className="mt-1 w-full"
                      />
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">{f.help}</p>
                    {!isImage && !isSelect && !value.trim() ? (
                      <p className="mt-1 break-words text-xs text-muted-foreground/80">
                        <span className="font-medium">Example:</span> {f.demo}
                      </p>
                    ) : null}
                    {err ? <p className="mt-1 text-xs text-destructive">{err}</p> : null}
                  </div>
                );
              })}
            </div>

            {section.ctaPreview ? (
              <div className="mt-4 rounded-md border bg-muted/30 p-3">
                <p className="text-xs font-medium text-muted-foreground">Button preview</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {section.ctaPreview.map((cta, i) => {
                    const text = (form[cta.textKey] ?? "").trim();
                    const link = (form[cta.linkKey] ?? "").trim();
                    return (
                      <div key={cta.label} className="flex min-w-0 items-center gap-2">
                        <span
                          className={
                            i === 0
                              ? "inline-flex max-w-[220px] items-center truncate rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                              : "inline-flex max-w-[220px] items-center truncate rounded-md border bg-background px-4 py-2 text-sm font-medium text-foreground"
                          }
                        >
                          {text || "Button label"}
                        </span>
                        <span className="min-w-0 truncate text-xs text-muted-foreground">
                          → {link || "no link set"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className="mt-4 flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => fillDemo(section)}>
                <Wand2 className="mr-2 h-3.5 w-3.5" aria-hidden />
                Fill empty fields with sample content
              </Button>
            </div>
          </AdminSection>
        ))}

        {LINKED_SECTIONS.map((section, i) => (
          <AdminSection
            key={section.title}
            number={SECTIONS.length + i + 1}
            icon={section.icon}
            title={section.title}
            description={section.description}
            defaultOpen={false}
          >
            <Button asChild variant="outline" size="sm">
              <Link to={section.to}>
                {section.cta}
                <ExternalLink className="ml-2 h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          </AdminSection>
        ))}
      </div>

      <div className="sticky bottom-0 mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border/60 bg-background/95 py-4 backdrop-blur">
        <Button variant="ghost" onClick={handleReset} disabled={!dirty || mutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            !dirty ||
            mutation.isPending ||
            Object.values(imageErrors).some(Boolean)
          }
        >
          {mutation.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
